import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChevronDown, ShieldCheck, BadgeCheck, PaintRoller } from "lucide-react";
import { PHONE, PHONE_HREF, LICENSE, scrollToSection } from "./data";

const lineReveal = {
  hidden: { y: "112%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.95, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const BADGES = [
  { icon: BadgeCheck, text: "20+ YEARS OF EXPERIENCE" },
  { icon: ShieldCheck, text: LICENSE.toUpperCase() },
  { icon: PaintRoller, text: "FREE ESTIMATES" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-[100svh] items-center overflow-hidden bg-navy-dark"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110" aria-hidden="true">
        <img
          src="/assets/hero.webp"
          alt="Freshly painted house exterior with espresso-brown trim by Teo's Painting LLC in Reno NV"
          className="h-full w-full object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/95 via-navy-dark/70 to-navy-dark/25" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-navy-dark to-transparent" aria-hidden="true" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-32 sm:px-8">
        <motion.p
          variants={fadeUp}
          custom={0.1}
          initial="hidden"
          animate="visible"
          className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold sm:text-xs"
          data-testid="hero-overline"
        >
          Teo's Painting LLC · Reno — Sparks — Carson City
        </motion.p>

        <h1 className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" variants={lineReveal} custom={0} initial="hidden" animate="visible">
              PROFESSIONAL PAINTING.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="text-gold-grad block font-display font-bold italic"
              variants={lineReveal}
              custom={1}
              initial="hidden"
              animate="visible"
            >
              BEAUTIFUL RESULTS.
            </motion.span>
          </span>
        </h1>

        <motion.p
          variants={fadeUp}
          custom={0.65}
          initial="hidden"
          animate="visible"
          className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
          data-testid="hero-subtext"
        >
          Quality painting and refinishing backed by over 20 years of experience.
          Proudly serving Reno, Sparks &amp; Carson City.
        </motion.p>

        <motion.div
          variants={fadeUp}
          custom={0.8}
          initial="hidden"
          animate="visible"
          className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-estimate-cta"
            onClick={() => scrollToSection("#contact")}
            className="rounded-full bg-gold px-8 py-4 text-[13px] font-extrabold tracking-[0.14em] text-navy shadow-[0_18px_40px_-12px_rgba(212,175,55,0.55)] transition-[background-color,transform,box-shadow] duration-200 hover:scale-[1.04] hover:bg-gold-light"
          >
            GET A FREE ESTIMATE
          </button>
          <a
            href={PHONE_HREF}
            data-testid="hero-call-cta"
            className="flex items-center justify-center gap-2.5 rounded-full border border-white/30 px-8 py-4 text-[13px] font-bold tracking-[0.14em] text-white backdrop-blur-sm transition-[border-color,color,background-color] duration-200 hover:border-gold hover:text-gold"
          >
            <Phone size={16} /> CALL {PHONE}
          </a>
        </motion.div>

        <motion.div
          variants={fadeUp}
          custom={0.95}
          initial="hidden"
          animate="visible"
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4"
          data-testid="hero-trust-badges"
        >
          {BADGES.map(({ icon: Icon, text }) => (
            <span key={text} className="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.2em] text-white/60">
              <Icon size={16} className="text-gold" />
              {text}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#services")}
        aria-label="Scroll to services"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-white/50 transition-colors hover:text-gold"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} className="block">
          <ChevronDown size={26} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
